import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BroadcastAction,
  getV1CompaniesCompanyIdJobsJobIdLoads,
  getV1CompaniesCompanyIdJobsJobIdLoadsCycles,
  GetV1CompaniesCompanyIdJobsJobIdLoadsData,
  getV1CompaniesCompanyIdJobsJobIdTickets,
  GetV1CompaniesCompanyIdJobsJobIdTicketsData,
  getV1CompaniesCompanyIdTicketsId,
  JobLoadActivity_Broadcast,
} from '@treadinc/horizon-api-spec';
import { noop } from 'lodash-es';
import React, { createContext, useCallback, useEffect, useId } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import { Job } from '~hooks/useJob/models';
import { Load, LoadCycle } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import {
  makeJobsBillingLinesQueryKey,
  makeJobsQueryContextKey,
  makeLoadsQueryContextBaseKey,
  makeLoadsQueryContextKey,
} from '~pages/Approvals/Files/utils';
import { realTimeChannels } from '~services/realTimeChannels';
import { LoadsJobFilters } from '~types/loadsFilters';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';
import { jobLoadActivityDataIsLoadRead } from '~utils/rtu/rtu-utils';

type LoadsQueryContextType = {
  clearQueryData: () => void;
  cycles?: LoadCycle[];
  findStoredTicketById: (ticketId: string) => Ticket | undefined;
  isFetchingLoadCycles: boolean;
  isFetchingLoads: boolean;
  isFetchingTickets: boolean;
  job: Job;
  loads?: Load[];
  refreshEverything: () => void;
  refreshLoads: () => void;
  refreshParentJob: (orderId?: string) => void;
  refreshTicketById: (ticketId: string) => void;
  removeStoredLoadById: (loadId: string) => void;
  removeStoredTicketsById: (ticketIds: string[]) => void;
  tickets?: Ticket[];
  updateStoredLoadById: (loadId: string, updatedLoad: Load) => void;
};

const LoadsQueryContext = createContext<LoadsQueryContextType>({
  clearQueryData: noop,
  findStoredTicketById: () => undefined,
  isFetchingLoadCycles: true,
  isFetchingLoads: true,
  isFetchingTickets: true,
  job: {} as Job,
  refreshEverything: noop,
  refreshLoads: noop,
  refreshParentJob: noop,
  refreshTicketById: noop,
  removeStoredLoadById: noop,
  removeStoredTicketsById: noop,
  updateStoredLoadById: noop,
});

interface LoadsQueryContextProviderProps {
  job: Job;
  companyId: string;
  filters?: LoadsJobFilters;
}

function LoadsQueryContextProvider({
  children,
  job,
  companyId,
  filters,
}: React.PropsWithChildren<LoadsQueryContextProviderProps>) {
  const effectiveCompanyId = useEffectiveCompanyId();
  const loadsQueryKey = makeLoadsQueryContextKey(job.id, 'loads');
  const loadCyclesQueryKey = makeLoadsQueryContextKey(job.id, 'load_cycles');
  const ticketsQueryKey = makeLoadsQueryContextKey(job.id, 'tickets');

  const queryClient = useQueryClient();
  const instanceId = useId();

  const loadsQuery = useQuery({
    queryKey: [loadsQueryKey, filters],
    queryFn: async ({ signal }) => {
      // FIXME @todo To be properly paginated in a follow-up PR.
      const query: GetV1CompaniesCompanyIdJobsJobIdLoadsData['query'] = {
        'page[limit]': MAX_API_PAGE_SIZE,
      };

      if (filters?.driverIds?.length) {
        query['filter[job][driver_ids]'] = filters.driverIds;
      }

      if (filters?.dropoffSiteIds?.length) {
        query['filter[job][dropoff_site_ids]'] = filters.dropoffSiteIds;
      }

      if (filters?.pickupSiteIds?.length) {
        query['filter[job][pickup_site_ids]'] = filters.pickupSiteIds;
      }

      if (filters?.states?.length) {
        query['filter[job][states]'] = filters.states;
      }

      if (filters?.assigned) {
        query['filter[job][assigned]'] = true;
      }

      if (filters?.vendorAccountIds?.length) {
        query['filter[job][vendor_account_ids]'] = filters.vendorAccountIds;
      }

      if (filters?.billingApproved !== undefined) {
        query['filter[billing_approved]'] = filters.billingApproved;
      }

      if (filters?.materialIds?.length) {
        query['filter[material_ids]'] = filters.materialIds;
      }

      if (filters?.ticketPotentialDuplicate !== undefined) {
        query['filter[ticket][potential_duplicate]'] = filters.ticketPotentialDuplicate;
      }

      const response = await getV1CompaniesCompanyIdJobsJobIdLoads({
        path: { 'company-id': companyId, 'job-id': job.id },
        query,
        signal,
      });

      const loads = response.data.data.map((load) => Load.parse(load));

      return loads;
    },
  });

  const loadCyclesQuery = useQuery({
    queryKey: [loadCyclesQueryKey],
    queryFn: async ({ signal }) => {
      const response = await getV1CompaniesCompanyIdJobsJobIdLoadsCycles({
        path: { 'company-id': companyId, 'job-id': job.id },
        signal,
      });

      const loadCycles = response.data.data.load_cycles.map((loadCycle) => {
        return LoadCycle.parse(loadCycle);
      });

      return loadCycles;
    },
  });

  const ticketsQuery = useQuery({
    queryKey: [ticketsQueryKey],
    queryFn: async ({ signal }) => {
      // FIXME @todo To be properly paginated in a follow-up PR.
      const query: GetV1CompaniesCompanyIdJobsJobIdTicketsData['query'] = {
        'page[limit]': MAX_API_PAGE_SIZE,
      };

      const response = await getV1CompaniesCompanyIdJobsJobIdTickets({
        path: { 'company-id': effectiveCompanyId, 'job-id': job.id },
        query,
        signal,
      });

      const tickets = response.data.data.map((ticket) => {
        return Ticket.parse(ticket);
      });

      return tickets;
    },
  });

  const clearQueryData = useCallback(() => {
    const baseQueryKey = makeLoadsQueryContextBaseKey(job.id);

    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          const keyAsString = String(key);

          return keyAsString.startsWith(baseQueryKey);
        });
      },
    });
  }, [job.jobId]);

  const findStoredTicketById = useCallback<LoadsQueryContextType['findStoredTicketById']>(
    (ticketId) => {
      return ticketsQuery.data?.find((ticket) => {
        return ticket.id === ticketId;
      });
    },
    [ticketsQuery.data],
  );

  const removeStoredLoadById = useCallback<LoadsQueryContextType['removeStoredLoadById']>(
    (loadId) => {
      queryClient.setQueryData([loadsQueryKey, filters], (oldLoads?: Load[]) => {
        return oldLoads?.filter((oldLoad) => {
          return oldLoad.id !== loadId;
        });
      });
    },
    [filters],
  );

  const removeStoredTicketsById = useCallback<
    LoadsQueryContextType['removeStoredTicketsById']
  >((ticketIds) => {
    queryClient.setQueryData([ticketsQueryKey], (oldTickets: Ticket[]) => {
      return oldTickets.filter((oldTicket) => {
        return !ticketIds.includes(oldTicket.id);
      });
    });
  }, []);

  const updateStoredLoadById = useCallback<LoadsQueryContextType['updateStoredLoadById']>(
    (loadId, updatedLoad) => {
      queryClient.setQueryData([loadsQueryKey, filters], (oldLoads?: Load[]) => {
        return oldLoads?.map((oldLoad) => {
          return oldLoad.id === loadId ? updatedLoad : oldLoad;
        });
      });
    },
    [filters],
  );

  const handleLoadActivityRTU = useCallback(
    (payload: JobLoadActivity_Broadcast) => {
      if (payload.action === BroadcastAction.DELETE) {
        removeStoredLoadById(payload.data.id);
        return;
      }

      if (payload.action === BroadcastAction.CREATE) {
        loadsQuery.refetch();
        return;
      }

      if (jobLoadActivityDataIsLoadRead(payload.data)) {
        const updatedLoad = Load.parse(payload.data);
        updateStoredLoadById(updatedLoad.id, updatedLoad);
      }
    },
    [loadsQuery.refetch, removeStoredLoadById, updateStoredLoadById],
  );

  useEffect(() => {
    const loadActivityKey = `${instanceId}_load_activity`;

    subscribeWithMultipleHandlers(
      realTimeChannels.JobLoadActivityChannel,
      companyId,
      loadActivityKey,
      handleLoadActivityRTU,
      { job_id: job.id },
    );

    return () => {
      unsubscribeHandler(
        realTimeChannels.JobLoadActivityChannel,
        companyId,
        loadActivityKey,
        { job_id: job.id },
      );
    };
  }, [companyId, handleLoadActivityRTU, instanceId, job.id]);

  const refreshTicketById = useCallback<LoadsQueryContextType['refreshTicketById']>(
    (ticketId) => {
      getV1CompaniesCompanyIdTicketsId({
        path: { 'company-id': effectiveCompanyId, id: ticketId },
      }).then((response) => {
        const updatedTicket = Ticket.parse(response.data.data);

        queryClient.setQueryData([ticketsQueryKey], (oldTickets: Ticket[]) => {
          return oldTickets.map((oldTicket) => {
            return oldTicket.id === updatedTicket.id ? updatedTicket : oldTicket;
          });
        });
      });
    },
    [effectiveCompanyId, queryClient, ticketsQueryKey],
  );

  const refreshParentJob = useCallback(
    (orderId?: string) => {
      const targetOrderId = orderId ?? job.order?.id;
      if (targetOrderId) {
        const jobsQueryKey = makeJobsQueryContextKey(String(targetOrderId));
        queryClient.invalidateQueries({ queryKey: [jobsQueryKey] });
        queryClient.invalidateQueries({
          queryKey: [makeJobsBillingLinesQueryKey(String(targetOrderId))],
        });
      }
    },
    [job.order?.id, queryClient],
  );

  const refreshEverything = useCallback<
    LoadsQueryContextType['refreshEverything']
  >(() => {
    loadsQuery.refetch();
    loadCyclesQuery.refetch();
    ticketsQuery.refetch();
  }, [loadsQuery, loadCyclesQuery, ticketsQuery]);

  const isFetchingLoadCycles =
    loadCyclesQuery.isFetching && !loadCyclesQuery.isRefetching;
  const isFetchingLoads = loadsQuery.isFetching && !loadsQuery.isRefetching;
  const isFetchingTickets = ticketsQuery.isFetching && !ticketsQuery.isRefetching;

  return (
    <LoadsQueryContext.Provider
      value={{
        clearQueryData,
        cycles: loadCyclesQuery.data,
        findStoredTicketById,
        isFetchingLoadCycles,
        isFetchingLoads,
        isFetchingTickets,
        job,
        loads: loadsQuery.data,
        refreshEverything,
        refreshLoads: loadsQuery.refetch,
        refreshParentJob,
        refreshTicketById,
        removeStoredLoadById,
        removeStoredTicketsById,
        tickets: ticketsQuery.data,
        updateStoredLoadById,
      }}
    >
      {children}
    </LoadsQueryContext.Provider>
  );
}

export default LoadsQueryContext;
export { LoadsQueryContextProvider };
